import { cur } from '@/lib/currency';
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Banknote, Landmark, Users, ArrowRight, CheckCircle2, AlertTriangle, Loader2, Wallet } from 'lucide-react';
import api from '@/lib/api';
import { useCategorySearch } from '@/hooks/useCategorySearch';
import { toast } from 'sonner';
import { localToday, toLocalYmd } from '@/lib/dateRange';

/**
 * BankPaymentDialog — unified "record a payment from any bank" form.
 *
 * Workflow:
 *   1. Pick paying branch + bank account (or 'cash' mode)
 *   2. Pick payee type (supplier / customer / employee / partner /
 *      expense category / online platform / ad-hoc)
 *   3. Pick the specific entity from a searchable dropdown
 *   4. Optionally see the per-branch open balance for that entity
 *      (auto-loaded for supplier/customer) and ALLOCATE the payment
 *      across branches with live "remaining" math
 *   5. Submit → master `bank_payments` row + downstream supplier_payments
 *      / expenses rows are created atomically
 *
 * Optional `seedFromTxn` prop pre-fills the dialog from a clicked
 * bank-statement transaction row, so the audit chain
 * "bank statement → bank payment → supplier ledger" is captured by ID.
 */
const PAYEE_TYPES = [
  { key: 'supplier', label: 'Supplier', icon: Banknote, color: 'bg-orange-100 text-orange-700' },
  { key: 'customer', label: 'Customer', icon: Users, color: 'bg-info/10 text-info' },
  { key: 'employee', label: 'Employee', icon: Users, color: 'bg-success/10 text-success' },
  { key: 'partner', label: 'Partner', icon: Users, color: 'bg-purple-100 text-purple-700' },
  { key: 'expense_category', label: 'Expense Cat.', icon: Wallet, color: 'bg-warning/10 text-warning' },
  { key: 'platform', label: 'Online Platform', icon: Landmark, color: 'bg-rose-100 text-rose-700' },
  { key: 'ad_hoc', label: 'Other / Ad-hoc', icon: Wallet, color: 'bg-stone-100 text-stone-700' },
];

const fmtSR = (n) => `${cur()} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function BankPaymentDialog({ open, onOpenChange, seedFromTxn, seedPayee, onCreated }) {
  // Static option lists (lazy-loaded the first time the dialog opens)
  const [branches, setBranches] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payeeOptions, setPayeeOptions] = useState([]); // depends on payee_type

  // Form state
  const [direction, setDirection] = useState('outgoing');
  const [paymentMode, setPaymentMode] = useState('bank');
  const [payingBranch, setPayingBranch] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [payeeType, setPayeeType] = useState('supplier');
  const [payeeId, setPayeeId] = useState('');
  const [payeeLabelOverride, setPayeeLabelOverride] = useState(''); // for ad_hoc
  const [amount, setAmount] = useState('');
  const [payeeSearch, setPayeeSearch] = useState('');
  const { match: catMatch } = useCategorySearch();
  const [date, setDate] = useState(localToday());
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');

  // Per-branch allocation
  const [balance, setBalance] = useState(null); // {total_balance, by_branch:[]}
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [allocations, setAllocations] = useState({}); // {branch_id: amount}

  // iter395h — Recent bank transactions for the selected payee. Loaded
  // when the user picks a payee so they can see *what we've already
  // recorded for them* — useful to avoid duplicate entries and spot
  // which past statement lines link to this payee. Limited to 10.
  const [recentTxns, setRecentTxns] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  // iter423 — Suggested unmatched bank-statement txns for this payee.
  const [suggestedStmtTxns, setSuggestedStmtTxns] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  // iter424 — Multi-select state for "Use these N at once".
  const [selectedStmtTxns, setSelectedStmtTxns] = useState({}); // key: `${sid}-${idx}`
  const [bulkBusy, setBulkBusy] = useState(false);

  // iter432 — User-selectable lookback window for the
  // "Suggested unlinked bank txns" panel. Default 365 (1 year)
  // since users often only start linking historical statements
  // months after import.
  const [lookbackDays, setLookbackDays] = useState(365);

  const [submitting, setSubmitting] = useState(false);

  // ── Reset on open ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setDirection('outgoing');
    setPaymentMode('bank');
    setPayingBranch('');
    setBankAccount('');
    // `seedPayee` lets callers (e.g. supplier/employee detail pages)
    // pre-scope the dialog to a specific party. seedFromTxn (statement
    // row) wins for amount/date/description as before.
    // iter449 — seedFromTxn may also carry: payee_type, category_hint,
    // amount/description overrides (used by the POS-Machines "Pay VAT
    // on bank fees" quick action).
    setPayeeType(seedFromTxn?.payee_type || seedPayee?.payee_type || 'supplier');
    setPayeeId(seedPayee?.payee_id || '');
    setPayeeLabelOverride(seedPayee?.payee_label || '');
    setAmount(seedFromTxn?.amount ? String(seedFromTxn.amount) : '');
    setDate(seedFromTxn?.date ? toLocalYmd(new Date(seedFromTxn.date)) : localToday());
    setDescription(seedFromTxn?.description || (seedPayee?.payee_label ? `Payment to ${seedPayee.payee_label}` : ''));
    setReference('');
    setPayeeSearch(seedFromTxn?.category_hint || '');
    setBalance(null);
    setAllocations({});
  }, [open, seedFromTxn, seedPayee]);

  // ── Lazy-load static lists once on first open ──────────────────────
  useEffect(() => {
    if (!open || branches.length > 0) return;
    Promise.all([
      api.get('/branches').catch(() => ({ data: [] })),
      api.get('/bank-accounts').catch(() => ({ data: [] })),
    ]).then(([b, ba]) => {
      setBranches(Array.isArray(b.data) ? b.data : (b.data?.branches || []));
      setBankAccounts(Array.isArray(ba.data) ? ba.data : (ba.data?.accounts || []));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Refresh payee dropdown when type changes ───────────────────────
  useEffect(() => {
    if (!open) return;
    if (payeeType === 'ad_hoc') { setPayeeOptions([]); setPayeeId(''); return; }
    setPayeeId('');
    setPayeeOptions([]);
    // iter447 — `expense_category` reads from /accounting/expense-category-tree
    // (the same rich tree the Expenses page uses) so the dropdown shows
    // every default + tenant-mapped category, not just the empty
    // legacy `expense_categories` collection.
    if (payeeType === 'expense_category') {
      api.get('/accounting/expense-category-tree')
        .then(r => {
          // Accept either {tree: [...]} or [...] shape for resilience.
          const tree = Array.isArray(r.data) ? r.data : (r.data?.tree || r.data?.categories || []);
          const opts = [];
          for (const m of tree) {
            const mainKey = (m.raw || m.name || '').toLowerCase();
            const mainLabel = m.account_name
              ? `${m.name}  →  ${m.account_name}`
              : m.name;
            opts.push({ id: mainKey, label: mainLabel });
            for (const s of (m.subcategories || [])) {
              const subKey = (s.raw || s.name || '').toLowerCase() || mainKey;
              opts.push({ id: subKey, label: `   ↳ ${s.name}` });
            }
          }
          setPayeeOptions(opts);
        })
        .catch(() => setPayeeOptions([]));
      return;
    }
    api.get(`/beneficiaries/link-options/${payeeType}`)
      .then(r => setPayeeOptions(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPayeeOptions([]));
  }, [open, payeeType]);

  // ── Fetch per-branch balance when payee resolved (for supplier/customer) ──
  useEffect(() => {
    if (!open || !payeeId || (payeeType !== 'supplier' && payeeType !== 'customer')) {
      setBalance(null);
      return;
    }
    setBalanceLoading(true);
    api.get(`/bank-payments/payee-balance?payee_type=${payeeType}&payee_id=${encodeURIComponent(payeeId)}`)
      .then(r => setBalance(r.data || null))
      .catch(() => setBalance(null))
      .finally(() => setBalanceLoading(false));
  }, [open, payeeType, payeeId]);

  // iter715 Phase B — Pull supplier open bills so the user can split a
  // single bank payment across N specific bills across branches.
  const [openBills, setOpenBills] = useState(null); // {total_outstanding, by_branch: [{branch_id, bills: [...]}]}
  const [invoiceSplits, setInvoiceSplits] = useState({}); // {branch_id: {bill_id: amount}}
  useEffect(() => {
    if (!open || payeeType !== 'supplier' || !payeeId) { setOpenBills(null); setInvoiceSplits({}); return; }
    api.get(`/suppliers/${payeeId}/open-bills`)
      .then((r) => setOpenBills(r.data || null))
      .catch(() => setOpenBills(null));
  }, [open, payeeType, payeeId]);

  // iter395h — Fetch the last 10 bank-payments and statement-linked
  // rows for the selected payee. Uses the existing /banking/ledger
  // endpoint which already groups bank_payments + linked statement_txns
  // and supports payee_type/payee_id filters.
  useEffect(() => {
    if (!open || !payeeId || payeeType === 'ad_hoc') {
      setRecentTxns([]);
      return;
    }
    setRecentLoading(true);
    api.get('/banking/ledger', {
      params: { payee_type: payeeType, payee_id: payeeId, period: 'all', limit: 10 },
    })
      .then(r => setRecentTxns(r.data?.rows || []))
      .catch(() => setRecentTxns([]))
      .finally(() => setRecentLoading(false));
  }, [open, payeeType, payeeId]);

  // iter423 — Fetch unmatched bank-statement txns for this payee in
  // parallel with the existing /banking/ledger call. Skipped for
  // payee types that don't have entity records (expense_category,
  // ad_hoc) since the matcher needs a name + bank_accounts list.
  useEffect(() => {
    if (!open || !payeeId || !['supplier', 'customer', 'employee', 'partner', 'platform'].includes(payeeType)) {
      setSuggestedStmtTxns([]);
      setSelectedStmtTxns({});
      return;
    }
    setSuggestLoading(true);
    setSelectedStmtTxns({});
    api.get('/banking/statement-txn/match-payee', {
      // iter561 — Pass `direction` so the supplier-payment dialog only
      // sees outgoing debits (and customer-receipt dialog only incoming
      // credits). Without this filter incoming SARIE payments showed up
      // mixed in with outgoing supplier-payment suggestions.
      params: {
        payee_type: payeeType,
        payee_id: payeeId,
        days: lookbackDays,
        limit: 50,
        direction: direction || 'outgoing',
      },
    })
      .then(r => setSuggestedStmtTxns(r.data?.rows || []))
      .catch(() => setSuggestedStmtTxns([]))
      .finally(() => setSuggestLoading(false));
  }, [open, payeeType, payeeId, lookbackDays, direction]);

  // iter424 — Bulk-create N bank payments from the selected txns.
  const selectedKeys = Object.keys(selectedStmtTxns).filter((k) => selectedStmtTxns[k]);
  const selectedTotal = suggestedStmtTxns
    .filter((s) => selectedStmtTxns[`${s.statement_id}-${s.txn_index}`])
    .reduce((a, b) => a + (b.amount || 0), 0);

  const useSelectedBulk = async () => {
    if (selectedKeys.length === 0) return;
    setBulkBusy(true);
    try {
      const txnRefs = suggestedStmtTxns
        .filter((s) => selectedStmtTxns[`${s.statement_id}-${s.txn_index}`])
        .map((s) => ({ statement_id: s.statement_id, txn_index: s.txn_index }));
      const r = await api.post('/banking/bank-payments/bulk-from-stmt-txns', {
        payee_type: payeeType,
        payee_id: payeeId,
        paying_branch_id: payingBranch || null,
        paying_bank_account_id: bankAccount || null,
        txn_refs: txnRefs,
        description,
      });
      const d = r.data || {};
      if (d.created_count > 0) {
        toast.success(`Created ${d.created_count} bank payment${d.created_count > 1 ? 's' : ''} totalling ${cur()} ${d.total_amount?.toLocaleString?.() || d.total_amount}`);
      }
      if (d.skipped_count > 0) {
        toast.info(`${d.skipped_count} txn(s) skipped (already linked or invalid)`);
      }
      // Refresh suggested list to drop the newly-linked rows.
      setSelectedStmtTxns({});
      const refresh = await api.get('/banking/statement-txn/match-payee', {
        params: {
          payee_type: payeeType,
          payee_id: payeeId,
          days: lookbackDays,
          limit: 50,
          direction: direction || 'outgoing',
        },
      });
      setSuggestedStmtTxns(refresh.data?.rows || []);
      // Notify parent so the wider page can refetch (e.g. ledger row count).
      if (typeof onCreated === 'function') onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk create failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const filteredPayees = useMemo(() => {
    const s = payeeSearch.trim().toLowerCase();
    return s ? payeeOptions.filter(p => (p.label || '').toLowerCase().includes(s)
      || (payeeType === 'expense_category' && !!catMatch(p.id, s))) : payeeOptions;
  }, [payeeOptions, payeeSearch, payeeType, catMatch]);

  const allocSum = useMemo(
    () => Object.values(allocations).reduce((s, v) => s + (Number(v) || 0), 0),
    [allocations]
  );
  const remaining = useMemo(() => {
    const a = parseFloat(amount) || 0;
    return Math.round((a - allocSum) * 100) / 100;
  }, [amount, allocSum]);
  const showAllocator = balance && balance.by_branch && balance.by_branch.length > 1;

  // Fill each branch box with exactly what that branch owes, capped by the
  // entered payment amount (largest-owed first). Mirrors the per-bill FIFO
  // button but at the branch level — "fill based on what each branch owes".
  const fillByOwed = () => {
    if (!balance?.by_branch?.length) return;
    let remainingAmt = parseFloat(amount) || balance.total_balance || 0;
    const next = {};
    const ordered = [...balance.by_branch].sort(
      (a, b) => Math.max(0, b.balance) - Math.max(0, a.balance)
    );
    for (const b of ordered) {
      const key = b.branch_id || '__unassigned__';
      const owed = Math.max(0, b.balance);
      const take = Math.min(owed, Math.max(0, remainingAmt));
      next[key] = Math.round(take * 100) / 100;
      remainingAmt = Number((remainingAmt - take).toFixed(2));
    }
    setAllocations(next);
  };

  // Pre-fill allocations the moment a balance arrives — admin can tweak
  // iter588 — Also re-run when `amount` changes (e.g., user picks a
  // bank-statement match that calls setAmount). Previously the pre-fill
  // only ran on balance load → if the user changed the amount after,
  // allocations stayed stuck at the old proportional split, producing
  // an "Off by X" mismatch that blocked save.
  useEffect(() => {
    if (!balance?.by_branch?.length) return;
    const total = parseFloat(amount) || balance.total_balance || 0;
    const totalOpen = balance.by_branch.reduce((s, b) => s + Math.max(0, b.balance), 0);
    if (totalOpen <= 0) return;
    const next = {};
    balance.by_branch.forEach(b => {
      const share = total * (Math.max(0, b.balance) / totalOpen);
      next[b.branch_id || '__unassigned__'] = Math.round(share * 100) / 100;
    });
    setAllocations(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance, amount]);

  // ── Validation ─────────────────────────────────────────────────────
  const errors = [];
  if (paymentMode === 'bank' && !bankAccount) errors.push('Pick a bank account');
  if (!payingBranch) errors.push('Pick a paying branch');
  if (payeeType !== 'ad_hoc' && !payeeId) errors.push('Pick a payee');
  if (payeeType === 'ad_hoc' && !payeeLabelOverride.trim()) errors.push('Enter the payee name');
  if (!amount || parseFloat(amount) <= 0) errors.push('Enter the amount');
  if (showAllocator && Math.abs(remaining) > 0.5) errors.push(`Allocations off by ${cur()} ${Math.abs(remaining).toFixed(2)}`);

  const submit = async () => {
    if (errors.length) { toast.error(errors[0]); return; }
    setSubmitting(true);
    try {
      const payload = {
        direction,
        paying_branch_id: payingBranch,
        paying_bank_account_id: paymentMode === 'bank' ? bankAccount : null,
        payment_mode: paymentMode,
        payee_type: payeeType,
        payee_id: payeeType === 'ad_hoc' ? null : payeeId,
        payee_label: payeeLabelOverride || undefined,
        amount: parseFloat(amount),
        date,
        description,
        reference,
        source_statement_id: seedFromTxn?.statement_id || null,
        source_txn_index: typeof seedFromTxn?.txn_index === 'number' ? seedFromTxn.txn_index : null,
      };
      if (showAllocator) {
        payload.allocations = Object.entries(allocations)
          .filter(([, v]) => Number(v) > 0)
          .map(([k, v]) => {
            const branchKey = k === '__unassigned__' ? null : k;
            const alloc = { branch_id: branchKey, amount: Number(v) };
            // iter715 Phase B — Attach per-invoice splits if the user has
            // picked specific bills for this branch.
            const splitMap = invoiceSplits[k] || {};
            const splits = Object.entries(splitMap)
              .filter(([, sv]) => Number(sv) > 0)
              .map(([bill_id, sv]) => ({ invoice_id: bill_id, amount: Number(sv) }));
            if (splits.length > 0) alloc.invoice_splits = splits;
            return alloc;
          });
      }
      const res = await api.post('/bank-payments', payload);
      toast.success(`Bank payment recorded · ${fmtSR(res.data?.amount)}`);
      if (onCreated) onCreated(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record bank payment');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[92dvh] overflow-y-auto" data-testid="bank-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote size={18} /> Record Bank Payment
          </DialogTitle>
          <DialogDescription>
            Pay any party — supplier, customer, employee, partner, online platform — from a specific bank account, and optionally split across the branches that owe the money.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Direction</Label>
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger data-testid="bp-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="outgoing">Outgoing (we paid)</SelectItem>
                  <SelectItem value="incoming">Incoming (we received)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Mode</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger data-testid="bp-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Paying Branch *</Label>
              <Select value={payingBranch} onValueChange={setPayingBranch}>
                <SelectTrigger data-testid="bp-paying-branch"><SelectValue placeholder="Pick branch..." /></SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {paymentMode === 'bank' && (
              <div>
                <Label>Bank Account *</Label>
                <Select value={bankAccount} onValueChange={setBankAccount}>
                  <SelectTrigger data-testid="bp-bank-account"><SelectValue placeholder="Pick bank account..." /></SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map(b => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name || b.account_name || b.bank_name}{b.account_number ? ` · ${b.account_number}` : ''}
                        {typeof b.current_balance === 'number' ? ` · ${cur()} ${b.current_balance.toLocaleString()}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* iter832 — Live balance chip. Shows the picked account's
                    available balance + a warning if the payment amount
                    exceeds it, so cashiers don't overdraw. */}
                {bankAccount && (() => {
                  const picked = bankAccounts.find(b => b.id === bankAccount);
                  if (!picked) return null;
                  const bal = Number(picked.current_balance || 0);
                  const amt = parseFloat(amount) || 0;
                  const after = bal - amt;
                  const willOverdraw = amt > 0 && after < 0;
                  return (
                    <div
                      className={`mt-2 rounded-md px-2.5 py-2 text-xs border ${willOverdraw ? 'border-rose-300 bg-rose-50 text-rose-800 dark:bg-rose-950/30 dark:text-rose-200' : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-200'}`}
                      data-testid="bp-bank-balance-chip"
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-semibold">Available balance:</span>
                        <span className="tabular-nums font-bold">{cur()} {bal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      {amt > 0 && (
                        <div className="flex items-center justify-between gap-2 flex-wrap mt-1 pt-1 border-t border-current/20">
                          <span>After this payment ({cur()} {amt.toLocaleString(undefined, { minimumFractionDigits: 2 })}):</span>
                          <span className="tabular-nums font-bold">{cur()} {after.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {willOverdraw && (
                        <div className="mt-1 text-[10px] italic">⚠ This payment exceeds the available balance — the account will go negative.</div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {PAYEE_TYPES.map(t => {
              const Ico = t.icon;
              const selected = t.key === payeeType;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setPayeeType(t.key)}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs border transition-colors ${selected ? 'ring-2 ring-foreground/30 bg-foreground/5' : 'hover:bg-stone-50 dark:hover:bg-stone-800/50'} ${t.color}`}
                  data-testid={`bp-payee-type-${t.key}`}
                >
                  <Ico size={12} /> {t.label}
                </button>
              );
            })}
          </div>

          {payeeType === 'ad_hoc' ? (
            <div>
              <Label>Payee Name *</Label>
              <Input
                placeholder="e.g. Govt Office, Driver Refund"
                value={payeeLabelOverride}
                onChange={(e) => setPayeeLabelOverride(e.target.value)}
                data-testid="bp-payee-label"
              />
            </div>
          ) : (
            <div>
              <Label>Search payee</Label>
              <Input
                placeholder={`Type to filter ${PAYEE_TYPES.find(p => p.key === payeeType)?.label?.toLowerCase()}…`}
                value={payeeSearch}
                onChange={(e) => setPayeeSearch(e.target.value)}
                className="mb-2"
                data-testid="bp-payee-search"
              />
              <div className="max-h-32 overflow-y-auto border rounded-md p-1 space-y-0.5" data-testid="bp-payee-list">
                {filteredPayees.length === 0 ? (
                  // iter472 — Helpful empty-state. Tells users WHY the list is
                  // empty (whole collection vs search filter) and how to add
                  // the missing record without leaving this page.
                  payeeOptions.length === 0 ? (
                    <div className="p-3 text-xs text-muted-foreground space-y-1.5" data-testid="bp-payee-empty-empty">
                      <p>
                        No {PAYEE_TYPES.find(p => p.key === payeeType)?.label?.toLowerCase()} records yet for this tenant.
                      </p>
                      {payeeType === 'supplier' && <a href="/suppliers" className="text-info hover:underline">+ Add a supplier</a>}
                      {payeeType === 'customer' && <a href="/customers" className="text-info hover:underline">+ Add a customer</a>}
                      {payeeType === 'employee' && <a href="/employees" className="text-info hover:underline">+ Add an employee</a>}
                      {payeeType === 'partner'  && <a href="/partners"  className="text-info hover:underline">+ Add a partner</a>}
                      {payeeType === 'platform' && <a href="/online-platforms" className="text-info hover:underline">+ Add an online platform</a>}
                      {payeeType === 'expense_category' && <a href="/settings#categories" className="text-info hover:underline">+ Manage expense categories</a>}
                      <p className="text-[10px] italic pt-0.5">
                        Or pick <strong>Other / Ad-hoc</strong> above to record a one-off payee without creating a record.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground p-2" data-testid="bp-payee-empty-search">
                      No {PAYEE_TYPES.find(p => p.key === payeeType)?.label?.toLowerCase()} matches &ldquo;{payeeSearch}&rdquo;.
                    </p>
                  )
                ) : filteredPayees.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPayeeId(p.id)}
                    className={`w-full text-left px-2 py-1 rounded text-xs ${payeeId === p.id ? 'bg-foreground/10 font-semibold' : 'hover:bg-stone-50 dark:hover:bg-stone-800/50'}`}
                    data-testid={`bp-payee-row-${p.id}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {/* iter395h — Recent transactions panel. Auto-loads when
                  the user picks a payee. Lists the last 10 bank-payments
                  (and linked statement transactions) we've recorded for
                  this party so the user can spot duplicates and verify
                  which past entries are tied to this name. */}
              {payeeId && (
                <div className="mt-2 rounded-lg border border-info/30 bg-info/5 p-2" data-testid="bp-recent-txns">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-info">
                      Recent transactions for this payee
                      {recentTxns.length > 0 && <span className="ml-1.5 normal-case font-normal text-muted-foreground">({recentTxns.length})</span>}
                    </span>
                    {recentLoading && <Loader2 size={11} className="animate-spin text-info" />}
                  </div>
                  {!recentLoading && recentTxns.length === 0 && (
                    <div className="text-[10px] text-muted-foreground italic py-1">
                      No prior transactions recorded — this looks like a first-time entry for this payee.
                    </div>
                  )}
                  {recentTxns.length > 0 && (
                    <div className="max-h-44 overflow-y-auto -mx-1">
                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead className="text-[9px] uppercase text-muted-foreground bg-info/10 sticky top-0">
                            <tr>
                              <th className="text-left px-1.5 py-1">Date</th>
                              <th className="text-left px-1.5 py-1">Description</th>
                              <th className="text-left px-1.5 py-1">Bank / Mode</th>
                              <th className="text-left px-1.5 py-1">Status</th>
                              <th className="text-right px-1.5 py-1">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recentTxns.map((t, i) => {
                              const linked = (t.linked_downstream_ids?.length || 0) > 0 || (t.downstream?.length || 0) > 0;
                              return (
                                <tr key={i} className="border-t border-info/20 hover:bg-info/10">
                                  <td className="px-1.5 py-1 text-muted-foreground whitespace-nowrap">{(t.ts || '').slice(0, 10)}</td>
                                  <td className="px-1.5 py-1 truncate max-w-[180px]" title={t.description || ''}>{t.description || '-'}</td>
                                  <td className="px-1.5 py-1 text-muted-foreground capitalize text-[10px] truncate max-w-[100px]">{t.bank_account_name || t.payment_mode || '-'}</td>
                                  <td className="px-1.5 py-1">
                                    {linked ? (
                                      <span className="inline-flex items-center gap-0.5 text-emerald-700 text-[9px] font-semibold uppercase">
                                        <CheckCircle2 size={9} /> Linked
                                      </span>
                                    ) : (
                                      <span className="text-amber-700 text-[9px] font-semibold uppercase">Unlinked</span>
                                    )}
                                  </td>
                                  <td className={`px-1.5 py-1 text-right font-mono tabular-nums ${(t.direction === 'incoming' || t.amount > 0) ? 'text-success' : 'text-error'}`}>
                                    {fmtSR(Math.abs(t.amount || 0))}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* iter423/424 — Suggested unmatched bank-statement txns
                  for this payee. iter424 adds multi-select + "Use these
                  N at once" button so the user can convert a stack of
                  statement lines into linked bank payments in a single
                  click (frequent supplier-reconciliation use case). */}
              {payeeId && ['supplier', 'customer', 'employee', 'partner', 'platform'].includes(payeeType) && (
                <div className="mt-2 rounded-lg border border-amber-300/60 bg-amber-50 p-2" data-testid="bp-suggested-stmt-txns">
                  <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">
                      Suggested bank statement matches
                      {suggestedStmtTxns.length > 0 && <span className="ml-1.5 normal-case font-normal text-amber-700/80">({suggestedStmtTxns.length} unlinked)</span>}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {/* iter432 — Lookback range so users can find older,
                          previously-unlinked statement txns. */}
                      <select
                        value={lookbackDays}
                        onChange={(e) => setLookbackDays(parseInt(e.target.value, 10))}
                        className="text-[10px] h-6 rounded border border-amber-300 bg-white px-1.5 text-amber-900 focus:outline-none focus:ring-1 focus:ring-amber-500"
                        data-testid="bp-lookback-select"
                        title="How far back to search for unlinked bank txns"
                      >
                        <option value={90}>Last 3 months</option>
                        <option value={180}>Last 6 months</option>
                        <option value={365}>Last 12 months</option>
                        <option value={730}>Last 24 months</option>
                        <option value={1095}>Last 3 years</option>
                      </select>
                      {suggestLoading && <Loader2 size={11} className="animate-spin text-amber-700" />}
                      {/* iter562 — Quick "select high-confidence" helper */}
                      {!suggestLoading && suggestedStmtTxns.some((s) => s.confidence_tier === 'high') && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[10px] border-emerald-400 text-emerald-800 hover:bg-emerald-50"
                          onClick={() => {
                            const next = {};
                            suggestedStmtTxns.forEach((s) => {
                              if (s.confidence_tier === 'high') {
                                next[`${s.statement_id}-${s.txn_index}`] = true;
                              }
                            });
                            setSelectedStmtTxns(next);
                          }}
                          title="Tick every row that scored as High confidence (★★★★ or ★★★★★)"
                          data-testid="bp-select-high-confidence"
                        >
                          ★ Select high-confidence
                        </Button>
                      )}
                      {selectedKeys.length > 0 && (
                        <Button
                          type="button"
                          size="sm"
                          className="h-6 px-2 text-[10px] bg-amber-600 hover:bg-amber-700 text-white"
                          disabled={bulkBusy}
                          onClick={useSelectedBulk}
                          data-testid="bp-bulk-use-selected"
                        >
                          {bulkBusy
                            ? <><Loader2 size={11} className="animate-spin mr-1" /> Creating…</>
                            : <>Use these {selectedKeys.length} ({fmtSR(selectedTotal)})</>
                          }
                        </Button>
                      )}
                    </div>
                  </div>
                  {!suggestLoading && suggestedStmtTxns.length === 0 && (
                    <div className="text-[11px] text-amber-700/80 px-1 py-1">No unlinked bank statement transactions in the selected window.</div>
                  )}
                  {suggestedStmtTxns.length > 0 && (
                    <div className="max-h-44 overflow-y-auto -mx-1">
                      <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                          <thead className="text-[9px] uppercase text-amber-700/80 bg-amber-100/60 sticky top-0">
                            <tr>
                              <th className="px-1.5 py-1 w-6">
                                <input
                                  type="checkbox"
                                  aria-label="Select all"
                                  data-testid="bp-stmt-select-all"
                                  checked={
                                    suggestedStmtTxns.length > 0 &&
                                    suggestedStmtTxns.every((s) => selectedStmtTxns[`${s.statement_id}-${s.txn_index}`])
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const next = {};
                                      suggestedStmtTxns.forEach((s) => { next[`${s.statement_id}-${s.txn_index}`] = true; });
                                      setSelectedStmtTxns(next);
                                    } else {
                                      setSelectedStmtTxns({});
                                    }
                                  }}
                                />
                              </th>
                              <th className="text-left px-1.5 py-1">Date</th>
                              <th className="text-left px-1.5 py-1">Bank Description</th>
                              <th className="text-left px-1.5 py-1">Match</th>
                              <th className="text-center px-1.5 py-1" title="Confidence: how likely this row is the right link">Conf.</th>
                              <th className="text-right px-1.5 py-1">Amount</th>
                              <th className="px-1.5 py-1"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {suggestedStmtTxns.map((s) => {
                              const k = `${s.statement_id}-${s.txn_index}`;
                              return (
                                <tr key={k} className={`border-t border-amber-200 hover:bg-amber-100/60 ${selectedStmtTxns[k] ? 'bg-amber-100' : ''}`}>
                                  <td className="px-1.5 py-1">
                                    <input
                                      type="checkbox"
                                      aria-label={`Select ${s.description}`}
                                      data-testid={`bp-stmt-check-${k}`}
                                      checked={!!selectedStmtTxns[k]}
                                      onChange={(e) => setSelectedStmtTxns((prev) => ({ ...prev, [k]: e.target.checked }))}
                                    />
                                  </td>
                                  <td className="px-1.5 py-1 text-amber-900 whitespace-nowrap">{s.date}</td>
                                  <td className="px-1.5 py-1 truncate max-w-[200px] text-amber-900" title={s.description}>{s.description || '—'}</td>
                                  <td className="px-1.5 py-1">
                                    {(() => {
                                      // iter561 — Plain-English labels + tooltip.
                                      const v = s.match_via || '';
                                      const labelMap = {
                                        account: 'IBAN match',
                                        beneficiary: 'Beneficiary',
                                        linked: 'Auto-tagged',
                                        description: 'Name match',
                                      };
                                      const colourMap = {
                                        account: 'bg-emerald-100 text-emerald-700',
                                        beneficiary: 'bg-emerald-100 text-emerald-700',
                                        linked: 'bg-violet-100 text-violet-700',
                                        description: 'bg-blue-100 text-blue-700',
                                      };
                                      const label = labelMap[v] || v;
                                      const cls = colourMap[v] || 'bg-stone-100 text-stone-700';
                                      return (
                                        <span
                                          className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase cursor-help ${cls}`}
                                          title={s.match_reason || `Matched via ${v}`}
                                          data-testid={`bp-match-via-${v}`}
                                        >
                                          {label}
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td className="px-1.5 py-1 text-center">
                                    {(() => {
                                      // iter562 — Confidence column. Backend
                                      // returns a 0-100 score, tier, star count,
                                      // and reasons list. Render as star pill
                                      // tinted by tier with reasons tooltip.
                                      const stars = Math.max(1, Math.min(5, Number(s.confidence_stars) || 1));
                                      const tier = s.confidence_tier || 'low';
                                      const score = Number(s.confidence_score) || 0;
                                      const tint =
                                        tier === 'high' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                                        tier === 'medium' ? 'bg-amber-100 text-amber-800 border-amber-300' :
                                        'bg-stone-100 text-stone-600 border-stone-200';
                                      const reasons = (s.confidence_reasons || []).join(' · ');
                                      return (
                                        <span
                                          className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold cursor-help ${tint}`}
                                          title={reasons ? `Score ${score}/100\n${reasons}` : `Score ${score}/100`}
                                          data-testid={`bp-confidence-${tier}`}
                                        >
                                          <span className="tracking-tight">{'★'.repeat(stars)}{'☆'.repeat(5 - stars)}</span>
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  <td className={`px-1.5 py-1 text-right font-mono tabular-nums ${s.direction === 'incoming' ? 'text-success' : 'text-error'}`}>
                                    {fmtSR(Math.abs(s.amount || 0))}
                                  </td>
                                  <td className="px-1.5 py-1 text-right">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-6 px-2 text-[10px]"
                                      data-testid={`bp-use-stmt-${k}`}
                                      onClick={() => {
                                        setAmount(String(Math.abs(s.amount || 0)));
                                        if (s.date) setDate(s.date);
                                        if (s.description) setDescription(s.description);
                                        setReference(s.description?.slice(0, 24) || '');
                                        if (s.direction) setDirection(s.direction);
                                      }}
                                    >
                                      Use this
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label>Amount *</Label>
              <Input
                type="number" step="0.01" placeholder="0.00"
                value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="bp-amount"
              />
            </div>
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="bp-date" />
            </div>
            <div>
              <Label>Reference</Label>
              <Input
                placeholder="TRX-..."
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Input
              placeholder="What was this payment for?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="bp-description"
            />
          </div>

          {/* Per-branch allocation (only renders when payee is owed by 2+ branches) */}
          {balanceLoading && (
            <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Loading per-branch balance…
            </p>
          )}
          {showAllocator && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20 p-3 space-y-2" data-testid="bp-allocator">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-900 dark:text-amber-200">
                  Allocate across branches owed
                </div>
                <Badge variant="outline" className="text-[10px] bg-white/60 dark:bg-stone-900/60">
                  Total open: {fmtSR(balance.total_balance)}
                </Badge>
              </div>
              <div className="flex justify-end -mt-1">
                <button
                  type="button"
                  onClick={fillByOwed}
                  className="text-[10px] uppercase tracking-wide font-bold text-amber-800 hover:text-amber-950 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded px-2 py-0.5 transition-colors"
                  title="Fill each branch box with what it owes (largest first), capped by the payment amount"
                  data-testid="bp-fill-by-owed"
                >
                  ⇣ Fill by amount owed
                </button>
              </div>
              {balance.by_branch.map(b => {
                const key = b.branch_id || '__unassigned__';
                // iter715 Phase B — Pull open bills for THIS branch (when supplier).
                const branchBills = (openBills?.by_branch || []).find(x => (x.branch_id || '__unassigned__') === key)?.bills || [];
                const splitMap = invoiceSplits[key] || {};
                const splitSum = Object.values(splitMap).reduce((s, v) => s + (Number(v) || 0), 0);
                const allocAmt = Number(allocations[key] || 0);
                const splitDelta = allocAmt - splitSum;
                return (
                  <div key={key} className="text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-32 truncate font-medium">{b.branch_name || 'Unassigned'}</span>
                      <span className="w-24 text-muted-foreground">owed {fmtSR(b.balance)}</span>
                      <ArrowRight size={10} className="text-muted-foreground" />
                      <Input
                        type="number" step="0.01"
                        value={allocations[key] != null ? allocations[key] : ''}
                        onChange={(e) => setAllocations(a => ({ ...a, [key]: e.target.value }))}
                        className="h-7 w-28 text-xs"
                        data-testid={`bp-alloc-${key}`}
                      />
                    </div>
                    {/* iter715 Phase B — per-bill splits for supplier with open bills */}
                    {payeeType === 'supplier' && branchBills.length > 0 && allocAmt > 0 && (
                      <details className="mt-1 ml-32 pl-1 border-l-2 border-amber-300 dark:border-amber-700">
                        <summary className="cursor-pointer text-[11px] text-amber-800 dark:text-amber-200" data-testid={`bp-bills-summary-${key}`}>
                          Split across {branchBills.length} open bill{branchBills.length === 1 ? '' : 's'} ·
                          <span className={`ml-1 font-mono ${Math.abs(splitDelta) <= 0.5 && splitSum > 0 ? 'text-emerald-700' : ''}`}>
                            {splitSum > 0 ? `Picked ${fmtSR(splitSum)}` : 'optional'}
                          </span>
                        </summary>
                        <div className="mt-1 space-y-1">
                          {/* iter716 — FIFO auto-distribute. Sort by date ASC,
                              fill each bill's outstanding until allocation is
                              exhausted. Oldest unpaid → settled first, which is
                              the AP convention almost every cashier wants. */}
                          <div className="flex items-center justify-between gap-2 pl-2 pb-1">
                            <button
                              type="button"
                              onClick={() => {
                                const sorted = [...branchBills].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
                                let remaining = Number(allocAmt) || 0;
                                const out = {};
                                for (const b of sorted) {
                                  if (remaining <= 0.005) break;
                                  const take = Math.min(remaining, Number(b.outstanding) || 0);
                                  if (take > 0) {
                                    out[b.id] = Number(take.toFixed(2));
                                    remaining = Number((remaining - take).toFixed(2));
                                  }
                                }
                                setInvoiceSplits((m) => ({ ...m, [key]: out }));
                              }}
                              className="text-[10px] uppercase tracking-wide font-bold text-amber-800 hover:text-amber-950 bg-amber-100 hover:bg-amber-200 border border-amber-300 rounded px-2 py-0.5 transition-colors"
                              title="Fill bills oldest-first until the allocation is consumed"
                              data-testid={`bp-fifo-${key}`}
                            >
                              ⇣ Auto-distribute FIFO
                            </button>
                            {Object.keys(splitMap).length > 0 && (
                              <button
                                type="button"
                                onClick={() => setInvoiceSplits((m) => ({ ...m, [key]: {} }))}
                                className="text-[10px] text-muted-foreground hover:text-foreground"
                                title="Clear all bill splits for this branch"
                                data-testid={`bp-fifo-clear-${key}`}
                              >
                                clear
                              </button>
                            )}
                          </div>
                          {[...branchBills].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map((bill) => (
                            <div key={bill.id} className="flex items-center gap-2 pl-2">
                              <span className="flex-1 min-w-0 truncate text-[10px]">
                                {bill.date} · {bill.description || bill.ref || bill.id.slice(0, 8)} ·
                                <span className="text-muted-foreground"> outstanding {fmtSR(bill.outstanding)}</span>
                              </span>
                              <Input
                                type="number" step="0.01" max={bill.outstanding}
                                value={splitMap[bill.id] || ''}
                                onChange={(e) => setInvoiceSplits((m) => ({
                                  ...m,
                                  [key]: { ...(m[key] || {}), [bill.id]: e.target.value },
                                }))}
                                className="h-6 w-24 text-[10px]"
                                placeholder="0.00"
                                data-testid={`bp-bill-${key}-${bill.id}`}
                              />
                              <button
                                type="button"
                                onClick={() => setInvoiceSplits((m) => ({
                                  ...m,
                                  [key]: { ...(m[key] || {}), [bill.id]: bill.outstanding },
                                }))}
                                className="text-[10px] text-amber-700 hover:text-amber-900"
                                title="Pay this bill in full"
                              >
                                full
                              </button>
                            </div>
                          ))}
                          {splitSum > 0 && Math.abs(splitDelta) > 0.5 && (
                            <p className="text-[10px] text-rose-700 pl-2">
                              Bill splits ({fmtSR(splitSum)}) don&apos;t match this branch&apos;s allocation ({fmtSR(allocAmt)}). Off by {fmtSR(Math.abs(splitDelta))}.
                            </p>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-between items-center text-xs pt-1.5 border-t border-amber-200 dark:border-amber-800">
                <span className="text-muted-foreground">Allocated: <span className="font-bold tabular-nums">{fmtSR(allocSum)}</span></span>
                {Math.abs(remaining) <= 0.5 ? (
                  <span className="text-emerald-700 dark:text-emerald-400 inline-flex items-center gap-1 font-semibold">
                    <CheckCircle2 size={11} /> Balanced
                  </span>
                ) : (
                  <span className="text-rose-700 dark:text-rose-400 inline-flex items-center gap-1 font-semibold">
                    <AlertTriangle size={11} /> Off by {fmtSR(Math.abs(remaining))}
                  </span>
                )}
              </div>
            </div>
          )}

          {!showAllocator && balance && (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
              <CheckCircle2 size={11} /> Open balance for {balance.payee_type}: {fmtSR(balance.total_balance)}
              {balance.by_branch?.length === 1 && ` (Branch: ${balance.by_branch[0].branch_name || 'Unassigned'})`}
            </div>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || errors.length > 0}
            data-testid="bp-submit"
          >
            {submitting ? 'Recording…' : 'Record Bank Payment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
